import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
/** @jsxImportSource @emotion/react */
import { Box, Chip, IconButton, Stack } from '@mui/material';

import type { NotificationStack } from './types';

const getAccentColor = (variant: string): string => {
  switch (variant) {
    case 'success':
      return '#10b981';
    case 'error':
      return '#ef4444';
    case 'warning':
      return '#f59e0b';
    case 'default':
      return '#6b7280';
    default:
      return '#6b7280';
  }
};

interface StackableToastProps {
  stack: NotificationStack;
  onNavigate: (direction: 'prev' | 'next') => void;
  onRemoveMessage: (messageId: string) => void;
  onCloseStack: () => void;
}

export function StackableToast({
  stack,
  onNavigate,
  onRemoveMessage,
  onCloseStack,
}: StackableToastProps) {
  const currentMessage = stack.messages[stack.currentIndex];
  const hasMultipleMessages = stack.messages.length > 1;

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: '344px',
        maxWidth: '672px',
        background: '#313033',
        color: '#f4eff4',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '0.25px',
        padding: '14px 16px',
        borderRadius: '4px',
        border: 'none',
        borderLeft: `4px solid ${getAccentColor(stack.type)}`,
        boxShadow: `
          0px 10px 14px -7px rgba(0, 0, 0, 0.2),
          0px 22px 35px 3px rgba(0, 0, 0, 0.14),
          0px 8px 42px 7px rgba(0, 0, 0, 0.12)
        `,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-start',
          columnGap: 2,
          width: '100%',
        }}
      >
        {/* Message content */}
        <Box sx={{ flex: 1, minWidth: 0, mt: '4px' }}>
          {typeof currentMessage.content === 'string' ? (
            <span
              style={{
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.25px',
                wordBreak: 'break-word',
              }}
            >
              {currentMessage.content}
            </span>
          ) : (
            currentMessage.content
          )}
        </Box>

        {/* Navigation controls - show when there are multiple messages */}
        {hasMultipleMessages && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={() => onNavigate('prev')}
              size="small"
              sx={{
                color: '#f4eff4',
                padding: '4px',
                minWidth: '24px',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: 'rgba(244, 239, 244, 0.08)',
                },
                transition: 'background-color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              }}
            >
              <ChevronLeft sx={{ fontSize: '18px' }} />
            </IconButton>

            <Chip
              label={`${stack.currentIndex + 1}/${stack.messages.length}`}
              size="small"
              sx={{
                height: '20px',
                fontSize: '12px',
                backgroundColor: 'rgba(244, 239, 244, 0.1)',
                color: '#f4eff4',
                border: '1px solid rgba(244, 239, 244, 0.2)',
                '& .MuiChip-label': {
                  padding: '0 6px',
                },
              }}
            />

            <IconButton
              onClick={() => onNavigate('next')}
              size="small"
              sx={{
                color: '#f4eff4',
                padding: '4px',
                minWidth: '24px',
                minHeight: '24px',
                '&:hover': {
                  backgroundColor: 'rgba(244, 239, 244, 0.08)',
                },
                transition: 'background-color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              }}
            >
              <ChevronRight sx={{ fontSize: '18px' }} />
            </IconButton>
          </Stack>
        )}

        {/* Close button */}
        <IconButton
          onClick={(e) => {
            if (e.shiftKey || hasMultipleMessages === false) {
              // Shift+click or single message: close entire stack
              onCloseStack();
            } else {
              // Normal click with multiple messages: remove current message
              onRemoveMessage(currentMessage.id);
            }
          }}
          title={
            hasMultipleMessages ? 'Click to remove this message, Shift+Click to close all' : 'Close'
          }
          size="small"
          sx={{
            color: '#f4eff4',
            padding: '4px',
            minWidth: '24px',
            minHeight: '24px',
            '&:hover': {
              backgroundColor: 'rgba(244, 239, 244, 0.08)',
            },
            transition: 'background-color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
          }}
        >
          <Close sx={{ fontSize: '18px' }} />
        </IconButton>
      </Stack>
    </Box>
  );
}
